import React, { useState, useEffect, useRef } from "react"
import { Button } from "@blueprintjs/core"
import { twMerge } from "tailwind-merge"
import { useTranslation } from "react-i18next"

type UpdateReferenceRouteButtonProps = {
  isVisible: boolean
  onUpdate: () => void
  onStart: () => void
  onCancel: () => void
  message?: string // New optional prop for custom message
}

const UpdateReferenceRouteButton: React.FC<UpdateReferenceRouteButtonProps> = ({
  isVisible,
  onUpdate,
  onCancel,
  onStart,
  message, // Defaults to "Tracking will update in"
}) => {
  const { t } = useTranslation()
  const [countdown, setCountdown] = useState(10)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isVisible) {
      // Set the global flag when countdown starts
      window.isRouteUpdateTrackingCountdownActive = true

      setCountdown(10)
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current)
            }
            if (window.isRouteUpdateTrackingCountdownActive) {
              // Clear the global flag when countdown completes
              window.isRouteUpdateTrackingCountdownActive = false
              onUpdate()
            }
            if (window.isRouteStartTrackingCountdownActive) {
              window.isRouteStartTrackingCountdownActive = false
              onStart()
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      // Clear the global flag when button becomes invisible
      window.isRouteUpdateTrackingCountdownActive = false
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      // Clear the global flag when component unmounts
      window.isRouteUpdateTrackingCountdownActive = false
    }
  }, [isVisible, onStart, onUpdate])

  if (!isVisible) {
    return null
  }

  const handleCancel = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    // Clear the global flag when countdown is canceled
    if (window.isRouteUpdateTrackingCountdownActive) {
      window.isRouteUpdateTrackingCountdownActive = false
      onCancel()
    }
    if (window.isRouteStartTrackingCountdownActive) {
      window.isRouteStartTrackingCountdownActive = false
      onCancel()
    }
  }

  return (
    <Button
      className={twMerge("rounded-md !bg-yellow-500 !text-black")}
      title={t("maps.updateReferenceRouteButton.title")}
      onClick={handleCancel}
    >
      {t("maps.updateReferenceRouteButton.countdown", {
        message: message ?? t("maps.updateReferenceRouteButton.defaultMessage"),
        seconds: countdown,
      })}
    </Button>
  )
}

export default UpdateReferenceRouteButton
